import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { qualityApi, type QualityHazardItem } from '../services/quality';

type HazardDraft = {
  id: number | null;
  chapter: string;
  processStep: string;
  hazard: string;
  action: string;
};

function createDraft(item?: QualityHazardItem): HazardDraft {
  return {
    id: item?.id ?? null,
    chapter: item?.chapter ?? '',
    processStep: item?.processStep ?? '',
    hazard: item?.hazard ?? '',
    action: item?.action ?? '',
  };
}

function BackIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

function GearIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1.04 1.55V22a2 2 0 0 1-4 0v-.09a1.7 1.7 0 0 0-1.04-1.55 1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-1.55-1.04H3a2 2 0 0 1 0-4h.09A1.7 1.7 0 0 0 4.64 8.4a1.7 1.7 0 0 0-.34-1.87l-.06-.06A2 2 0 1 1 7.07 3.64l.06.06a1.7 1.7 0 0 0 1.87.34H9a1.7 1.7 0 0 0 1-1.55V2a2 2 0 0 1 4 0v.09a1.7 1.7 0 0 0 1.04 1.55 1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 19.4 8.4V8.5A1.7 1.7 0 0 0 20.95 9H21a2 2 0 0 1 0 4h-.09A1.7 1.7 0 0 0 19.4 15Z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18" />
      <path d="M8 6V4h8v2" />
      <path d="M19 6l-1 13a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </svg>
  );
}

function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-3">
      <div className="flex max-h-[calc(100vh-1.5rem)] w-full max-w-4xl flex-col overflow-hidden rounded-[24px] border border-slate-300 bg-white shadow-xl">
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <h2 className="text-base font-semibold text-slate-950">{title}</h2>
          <button type="button" className="btn btn-secondary px-2 py-1" onClick={onClose}>
            Stäng
          </button>
        </div>
        <div className="overflow-auto p-4">{children}</div>
      </div>
    </div>
  );
}

export function QualityHazardsPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState<QualityHazardItem[]>([]);
  const [chapterInput, setChapterInput] = useState('');
  const [chapterFilter, setChapterFilter] = useState('');
  const [draft, setDraft] = useState<HazardDraft | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<QualityHazardItem | null>(null);

  const writeEnabled = canWrite('quality');

  const loadHazards = async (chapter = chapterFilter) => {
    setLoading(true);
    try {
      const data = await qualityApi.hazards(chapter);
      setItems(data.items);
    } catch {
      toast.error('Kunde inte ladda faroanalysen');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadHazards(chapterFilter);
  }, [chapterFilter]);

  const filteredCountLabel = useMemo(() => {
    if (chapterFilter.trim()) {
      return `${items.length} rader för kapitel ${chapterFilter.trim()}`;
    }
    return `${items.length} rader totalt`;
  }, [items.length, chapterFilter]);

  const handleSave = async () => {
    if (!draft) {
      return;
    }
    if (!draft.chapter.trim() || !draft.processStep.trim() || !draft.hazard.trim() || !draft.action.trim()) {
      toast.error('Fyll i alla fält');
      return;
    }

    setSaving(true);
    try {
      if (draft.id) {
        await qualityApi.updateHazard(draft.id, draft);
        toast.success('Faroanalysrad uppdaterad');
      } else {
        await qualityApi.createHazard(draft);
        toast.success('Faroanalysrad skapad');
      }
      setDraft(null);
      await loadHazards(chapterFilter);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte spara faroanalysrad');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) {
      return;
    }
    setSaving(true);
    try {
      await qualityApi.deleteHazard(deleteTarget.id);
      toast.success('Faroanalysrad borttagen');
      setDeleteTarget(null);
      await loadHazards(chapterFilter);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ta bort faroanalysrad');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-3">
          <Link to="/quality" className="inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm transition hover:border-slate-400 hover:bg-slate-50">
            <BackIcon />
            <span>Tillbaka</span>
          </Link>
          <div className="space-y-2">
            <h1 className="text-[26px] font-semibold tracking-[-0.03em] text-slate-950 md:text-[30px]">Faroanalys</h1>
            <p className="max-w-3xl text-base leading-7 text-slate-600">
              HACCP-rader importerade från gamla ApisAI, nu i samma app och med redigering via modal i stället för separat gammal vy.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            className="btn btn-secondary rounded-2xl px-4 py-2"
            onClick={() => navigate('/quality/hazards/print')}
          >
            Skriv ut / PDF
          </button>
          {writeEnabled ? (
            <button type="button" className="btn btn-primary rounded-2xl px-4 py-2" onClick={() => setDraft(createDraft())}>
              + Ny
            </button>
          ) : null}
        </div>
      </div>

      <div className="rounded-[28px] border border-slate-300 bg-white p-4 shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
        <div className="flex flex-wrap items-end gap-3">
          <label className="space-y-1">
            <span className="block text-sm font-medium text-slate-700">Kapitel</span>
            <input
              type="text"
              value={chapterInput}
              onChange={(event) => setChapterInput(event.target.value)}
              placeholder="t.ex. 2.4"
              className="w-32"
            />
          </label>
          <button type="button" className="btn btn-secondary rounded-xl px-4 py-2" onClick={() => setChapterFilter(chapterInput.trim())}>
            Filtrera
          </button>
          <button
            type="button"
            className="btn btn-secondary rounded-xl px-4 py-2"
            onClick={() => {
              setChapterInput('');
              setChapterFilter('');
            }}
          >
            Rensa
          </button>
          <div className="ml-auto text-sm text-slate-500">{filteredCountLabel}</div>
        </div>
      </div>

      <div className="overflow-hidden rounded-[28px] border border-slate-300 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
        <div className="max-h-[calc(100dvh-280px)] overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 z-10 bg-white">
              <tr className="border-b border-slate-300 text-left text-slate-900">
                <th className="px-4 py-3 font-semibold">Kapitel</th>
                <th className="px-4 py-3 font-semibold">Processsteg</th>
                <th className="px-4 py-3 font-semibold">Fara</th>
                <th className="px-4 py-3 font-semibold">Förebyggande åtgärd</th>
                <th className="px-4 py-3 font-semibold text-right">Åtgärder</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-slate-500">Laddar faroanalys...</td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-slate-500">Inga faror hittades.</td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id} className="border-b border-slate-200 align-top last:border-b-0">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{item.chapter}</div>
                      {item.docTitle ? <div className="mt-1 text-xs text-slate-500">{item.docTitle}</div> : null}
                    </td>
                    <td className="px-4 py-3 whitespace-pre-line text-slate-800">{item.processStep}</td>
                    <td className="px-4 py-3 whitespace-pre-line text-slate-800">{item.hazard}</td>
                    <td className="px-4 py-3 whitespace-pre-line text-slate-800">{item.action}</td>
                    <td className="px-4 py-3">
                      {writeEnabled ? (
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary transition hover:bg-primary/15"
                            onClick={() => setDraft(createDraft(item))}
                            aria-label="Ändra rad"
                          >
                            <GearIcon />
                          </button>
                          <button
                            type="button"
                            className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-danger/10 text-danger transition hover:bg-danger/15"
                            onClick={() => setDeleteTarget(item)}
                            aria-label="Ta bort rad"
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      ) : (
                        <div className="text-right text-xs text-slate-400">Läsning</div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {draft ? (
        <Modal title={draft.id ? 'Ändra faroanalysrad' : 'Ny faroanalysrad'} onClose={() => setDraft(null)}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="space-y-1">
              <span className="block text-sm font-medium text-slate-700">Kapitel</span>
              <input
                type="text"
                value={draft.chapter}
                onChange={(event) => setDraft((current) => (current ? { ...current, chapter: event.target.value } : current))}
                placeholder="t.ex. 2.4"
              />
            </label>
            <label className="space-y-1 md:col-span-2">
              <span className="block text-sm font-medium text-slate-700">Processsteg</span>
              <input
                type="text"
                value={draft.processStep}
                onChange={(event) => setDraft((current) => (current ? { ...current, processStep: event.target.value } : current))}
              />
            </label>
            <label className="space-y-1 md:col-span-2">
              <span className="block text-sm font-medium text-slate-700">Fara</span>
              <textarea
                rows={4}
                value={draft.hazard}
                onChange={(event) => setDraft((current) => (current ? { ...current, hazard: event.target.value } : current))}
              />
            </label>
            <label className="space-y-1 md:col-span-2">
              <span className="block text-sm font-medium text-slate-700">Förebyggande åtgärd</span>
              <textarea
                rows={4}
                value={draft.action}
                onChange={(event) => setDraft((current) => (current ? { ...current, action: event.target.value } : current))}
              />
            </label>
          </div>
          <div className="mt-5 flex justify-end gap-3">
            <button type="button" className="btn btn-secondary rounded-xl px-4 py-2" onClick={() => setDraft(null)}>
              Avbryt
            </button>
            <button type="button" className="btn btn-success rounded-xl px-4 py-2" onClick={handleSave} disabled={saving}>
              Spara
            </button>
          </div>
        </Modal>
      ) : null}

      {deleteTarget ? (
        <Modal title="Ta bort faroanalysrad" onClose={() => setDeleteTarget(null)}>
          <div className="space-y-4">
            <p className="text-sm leading-6 text-slate-700">
              Rad för kapitel <span className="font-semibold text-slate-950">{deleteTarget.chapter}</span> kommer att tas bort.
            </p>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
              <div className="font-medium text-slate-950">{deleteTarget.processStep}</div>
              <div className="mt-1 line-clamp-3">{deleteTarget.hazard}</div>
            </div>
            <div className="flex justify-end gap-3">
              <button type="button" className="btn btn-secondary rounded-xl px-4 py-2" onClick={() => setDeleteTarget(null)}>
                Avbryt
              </button>
              <button type="button" className="btn btn-danger rounded-xl px-4 py-2" onClick={handleDelete} disabled={saving}>
                Ta bort
              </button>
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
